import { LeftOutlined, RightOutlined, SkypeOutlined } from '@ant-design/icons';
import { Menu, MenuProps, Space, Typography } from 'antd';
import {
  MenuItemGroupType as AntMenuItemGroupType,
  MenuItemType as AntMenuItemType,
  SubMenuType as AntSubMenuType,
  MenuDividerType,
} from 'antd/es/menu/hooks/useItems';
import { FC, useMemo } from 'react';
import { LinkProps } from 'react-router-dom';

import { useAdvertiser } from '../../../../components/AdvertiserProvider';
import InitialsAvatar from '../../../../components/InitialsAvatar';

import styles from './styles.module.less';

type MenuItemType = AntMenuItemType & {
  to?: LinkProps['to'];
  href?: string;
};

type SubMenuType = Omit<AntSubMenuType, 'children'> & {
  children: ItemType[];
};

type MenuItemGroupType = Omit<AntMenuItemGroupType, 'children'> & {
  children: ItemType[];
};

type ItemType =
  | MenuItemType
  | SubMenuType
  | MenuItemGroupType
  | MenuDividerType;

type SideMenuProps = MenuProps & {
  collapsed?: boolean;
  onToggle: () => void;
};

const HelpMenu: FC<SideMenuProps> = ({ collapsed, onToggle, ...rest }) => {
  const [
    {
      accountManager: { firstName, lastName, email, skype },
    },
  ] = useAdvertiser();

  const name = `${firstName} ${lastName}`;

  const items = useMemo(() => {
    const transform = (item: ItemType): ItemType => {
      if (item) {
        if ('href' in item) {
          return {
            ...item,
            key: (item.href as string) || item.key,
            label: item.href ? (
              <Typography.Link href={item.href} target="_blank">
                {item.label}
              </Typography.Link>
            ) : (
              item.label
            ),
          };
        }

        if ('children' in item) {
          return {
            ...item,
            children: item.children.map(transform),
          };
        }
      }

      return item;
    };

    return (
      [
        {
          className: styles.manager,
          href: `mailto:${email}`,
          icon: <InitialsAvatar>{name}</InitialsAvatar>,
          label: (
            <Space className={styles.manager} direction="vertical" size={0}>
              <span>Account Manager</span>
              <span style={{ fontSize: 8 }}>{name}</span>
            </Space>
          ),
          title: collapsed && (
            <Space direction="vertical" size={0}>
              <strong>Account Manager</strong>
              {name}
            </Space>
          ),
        },
        skype && {
          href: `skype:${skype}?chat`,
          icon: <SkypeOutlined />,
          label: 'Contact via Skype',
          title: collapsed && (
            <Space direction="vertical" size={0}>
              <strong>Contact via Skype</strong>
              {skype}
            </Space>
          ),
        },
        {
          icon: collapsed ? <RightOutlined /> : <LeftOutlined />,
          key: 'collapse',
          label: collapsed ? 'Expand' : 'Collapse',
          onClick: onToggle,
        },
      ] as ItemType[]
    )
      .map(transform)
      .filter(Boolean);
  }, [collapsed, email, name, onToggle, skype]);

  return (
    <Menu
      className={styles.menu}
      items={items}
      mode="vertical"
      selectedKeys={[]}
      theme="dark"
      {...rest}
    />
  );
};

HelpMenu.displayName = 'HelpMenu';

export default HelpMenu;
